import React from 'react';
import { TableHead } from '@/components/ui/table';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

type SortDirection = 'asc' | 'desc';

interface SortableTableHeaderProps<T extends string> {
  column: T;
  label: string;
  sortColumn: T | null;
  sortDirection: SortDirection;
  onSort: (column: T) => void;
  className?: string;
}

/**
 * Shared sortable table header component
 * Used in: KBDocumentsPage, KBEntityTypesPage, KBValidationPage
 */
function SortableTableHeader<T extends string>({
  column,
  label,
  sortColumn,
  sortDirection,
  onSort,
  className = '',
}: SortableTableHeaderProps<T>) {
  const getSortIcon = () => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="h-3 w-3 ml-1" />;
    }
    return sortDirection === 'asc'
      ? <ArrowUp className="h-3 w-3 ml-1" />
      : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  return (
    <TableHead
      className={`font-medium text-foreground py-4 cursor-pointer hover:bg-muted/50 select-none ${className}`}
      onClick={() => onSort(column)}
    >
      <div className="flex items-center">
        {label}
        {getSortIcon()}
      </div>
    </TableHead>
  );
}

/**
 * Hook for managing table sorting state
 */
export function useSortableTable<T extends string>(defaultColumn?: T | null) {
  const [sortColumn, setSortColumn] = React.useState<T | null>(defaultColumn ?? null);
  const [sortDirection, setSortDirection] = React.useState<SortDirection>('asc');

  const handleSort = React.useCallback((column: T) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  }, [sortColumn]);

  const sortData = React.useCallback(<D,>(
    data: D[],
    compareFn: (a: D, b: D, column: T, direction: SortDirection) => number
  ): D[] => {
    if (!sortColumn) return data;
    return [...data].sort((a, b) => compareFn(a, b, sortColumn, sortDirection));
  }, [sortColumn, sortDirection]);

  return {
    sortColumn,
    sortDirection,
    handleSort,
    sortData,
  };
}

/**
 * Get sort icon for non-component usage
 */
export function getSortIcon<T extends string>(
  column: T,
  sortColumn: T | null,
  sortDirection: SortDirection
): React.ReactNode {
  if (sortColumn !== column) {
    return <ArrowUpDown className="h-3 w-3 ml-1" />;
  }
  return sortDirection === 'asc'
    ? <ArrowUp className="h-3 w-3 ml-1" />
    : <ArrowDown className="h-3 w-3 ml-1" />;
}

export default SortableTableHeader;
